import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, User, Phone, Mail, ChevronRight, Search } from "lucide-react";

const TIPOS = ["Particular", "Garantia", "Frota", "Body Shop"];

export default function Clientes() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients", { params: q ? { q } : {} });
    setClients(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="space-y-5" data-testid="clientes-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Cadastro</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Clientes</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn"><Plus className="w-4 h-4" /> Novo Cliente</Button>
          </DialogTrigger>
          <NovoClienteDialog onCreated={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Buscar por nome, telefone, email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="client-search-input" />
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 bg-secondary/60 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="col-span-4">Nome</div>
          <div className="col-span-3">Contato</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-2">Criado em</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Nenhum cliente cadastrado.</div>
        ) : (
          clients.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/clientes/${c.id}`)}
              className="w-full flex md:grid md:grid-cols-12 md:gap-4 items-start md:items-center gap-3 px-4 py-3 border-t border-border hover:bg-accent/50 text-left transition-colors"
              data-testid={`client-row-${c.id}`}
            >
              <div className="md:col-span-4 flex items-center gap-2.5 min-w-0 flex-1 md:flex-none">
                <div className="w-9 h-9 md:w-8 md:h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold shrink-0">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="md:hidden text-[11px] text-muted-foreground truncate">
                    {c.phone || c.email || "—"} · {c.tipo}
                  </div>
                </div>
              </div>
              <div className="hidden md:block col-span-3 text-sm text-muted-foreground truncate">
                <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone || "—"}</div>
                <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{c.email || "—"}</div>
              </div>
              <div className="hidden md:block col-span-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{c.tipo}</span>
              </div>
              <div className="hidden md:block col-span-2 text-xs text-muted-foreground font-mono">{new Date(c.created_at).toLocaleDateString("pt-BR")}</div>
              <div className="md:col-span-1 flex md:justify-end items-center text-muted-foreground shrink-0">
                <ChevronRight className="w-4 h-4" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function NovoClienteDialog({ onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", tipo: "Particular", observacoes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Cliente criado");
      onCreated();
    } catch { toast.error("Erro ao criar"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="new-client-dialog">
      <DialogHeader>
        <DialogTitle className="font-display tracking-tight">Novo Cliente</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Nome *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Telefone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" />
          </div>
        </div>
        <div>
          <Label>Endereço</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div>
          <Label>Tipo</Label>
          <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
            <SelectTrigger data-testid="client-tipo-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Observações</Label>
          <Textarea rows={3} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} data-testid="client-save-btn">{saving ? "Salvando…" : "Salvar Cliente"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
